import { useState } from 'react'
import { steamGameImageUrl } from '@/lib/steam'

// Structural prop — accepts the full Game and the slim variant used by
// common-library. appId is optional so the slim shape (which only has
// `id`) still type-checks; the fallback CDN URL is computed from `id`
// when appId is absent.
interface GameCoverGame {
  id: string
  appId?: number
  title: string
  hue: number
  accent: number
  headerImageUrl?: string
  earlyAccess: boolean
}

type CoverSize = 'xs' | 'sm' | 'md' | 'lg' | 'portrait'

const SIZES: Record<CoverSize, { w: number; h: number }> = {
  xs:      { w: 32,  h: 15  },
  sm:      { w: 72,  h: 40  },
  md:      { w: 184, h: 86  },
  lg:      { w: 360, h: 168 },
  portrait:{ w: 88,  h: 130 },
}

const STEAM_VARIANT: Record<CoverSize, Parameters<typeof steamGameImageUrl>[1]> = {
  xs:       'capsule_sm',
  sm:       'capsule_sm',
  md:       'capsule',
  lg:       'header',
  portrait: 'portrait',
}

interface GameCoverProps {
  game: GameCoverGame
  size?: CoverSize
}

export function GameCover({ game, size = 'md' }: GameCoverProps) {
  const [imgError, setImgError] = useState(false)
  const [imgLoaded, setImgLoaded] = useState(false)

  const { w, h } = SIZES[size]
  const h1 = game.hue
  const h2 = game.accent
  const seed = game.id.charCodeAt(0) + game.id.charCodeAt(game.id.length - 1)
  const pattern = seed % 4
  const fallbackAppId = game.appId ?? (Number(game.id) || 0)
  const imgSrc =
    game.headerImageUrl ||
    (fallbackAppId > 0 ? steamGameImageUrl(fallbackAppId, STEAM_VARIANT[size]) : null)
  const showImg = imgSrc !== null && !imgError

  return (
    <div
      className="cg-cover"
      style={{
        width: w,
        height: h,
        background: `linear-gradient(135deg, oklch(0.32 0.12 ${h1}) 0%, oklch(0.18 0.08 ${h2}) 100%)`,
      }}
    >
      {/* Decorative fallback — visible only while the image is absent or
          loading. Once the image paints (object-fit: cover) it fully occludes
          the SVG, so hiding it then saves per-row compositor work. */}
      {!imgLoaded && (
      <svg
        viewBox="0 0 200 100"
        preserveAspectRatio="xMidYMid slice"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.45 }}
      >
        {pattern === 0 && (
          <g>
            <circle cx="160" cy="30" r="40" fill={`oklch(0.55 0.18 ${h2})`} opacity="0.5" />
            <circle cx="180" cy="80" r="25" fill={`oklch(0.7 0.2 ${h1})`} opacity="0.4" />
          </g>
        )}
        {pattern === 1 && (
          <g>
            <path d="M0,80 L60,20 L120,60 L200,10 L200,100 L0,100 Z" fill={`oklch(0.4 0.14 ${h2})`} opacity="0.6" />
            <path d="M0,90 L80,40 L140,70 L200,30 L200,100 L0,100 Z" fill={`oklch(0.25 0.1 ${h1})`} opacity="0.7" />
          </g>
        )}
        {pattern === 2 && (
          <g>
            {[...Array(8)].map((_, i) => (
              <rect key={i} x={20 + i * 22} y={30 + (i % 3) * 12} width="3" height="40" fill={`oklch(0.65 0.18 ${h2})`} opacity={0.3 + (i % 3) * 0.15} />
            ))}
            <circle cx="40" cy="35" r="12" fill={`oklch(0.7 0.2 ${h1})`} opacity="0.5" />
          </g>
        )}
        {pattern === 3 && (
          <g>
            <polygon points="100,20 140,60 100,80 60,60" fill={`oklch(0.55 0.16 ${h2})`} opacity="0.6" />
            <polygon points="100,30 130,60 100,72 70,60" fill={`oklch(0.7 0.2 ${h1})`} opacity="0.5" />
          </g>
        )}
      </svg>
      )}

      {showImg && (
        <img
          src={imgSrc!}
          alt=""
          loading="lazy"
          decoding="async"
          onLoad={() => setImgLoaded(true)}
          onError={() => setImgError(true)}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
        />
      )}

      {!showImg && size !== 'xs' && size !== 'sm' && size !== 'md' && (
        <div className="cg-cover-title">{game.title}</div>
      )}
      {game.earlyAccess && (size === 'sm' || size === 'md' || size === 'lg') && (
        <div className="cg-cover-ea">EA</div>
      )}
    </div>
  )
}
